import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, TrendingDownIcon } from 'lucide-react';
type ExitIntentModalProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
};
export function ExitIntentModal({
  open,
  onClose,
  onContinue
}: ExitIntentModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-40" data-mp="19b8mie9sc9xy">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-41" data-mp="19ca9nh42smw1">
          <div className="absolute inset-0 bg-bark-950/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-42" data-mp="1f0xm4xyzof58" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="exit-intent-title" className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-bark-900 p-6 shadow-2xl sm:p-8" initial={{
        opacity: 0,
        y: 24,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 26
      }} data-id="element-43" data-mp="1oqqza3es52bl">
            <button type="button" onClick={onClose} className="absolute right-3 top-3 rounded-full p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500" aria-label="Close" data-id="element-44" data-mp="2lg87ap18j0gg">
              <XIcon className="h-5 w-5" data-id="element-45" data-mp="1j5ya5vc79c9z" />
            </button>

            <span className="inline-flex items-center gap-2 rounded-full bg-ember-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-ember-400" data-id="element-46" data-mp="1n6i8h28ielxp">
              <TrendingDownIcon className="h-3.5 w-3.5" data-id="element-47" data-mp="vej35p9ha6se" />
              Wait — don't go
            </span>

            <h2 id="exit-intent-title" className="mt-4 font-serif text-2xl font-bold leading-tight text-white sm:text-3xl" data-id="element-48" data-mp="zfl2e63eaiud">
              You're about to save between{' '}
              <span className="text-ember-400" data-id="element-49" data-mp="195xldtvka3ye">$200 and $400 a month</span>.
            </h2>

            <p className="mt-3 text-base leading-relaxed text-white/70" data-id="element-50" data-mp="bqtg1vchkupi">
              Want to see if you qualify? It takes less than 60 seconds and
              there's no obligation.
            </p>

            <div className="mt-6 space-y-3" data-id="element-51" data-mp="963w4chs8pxk">
              <button type="button" onClick={onContinue} className="min-h-[56px] w-full touch-manipulation rounded-xl bg-ember-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-ember-700/30 transition-all hover:bg-ember-600 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-bark-900" data-id="element-52" data-mp="952ic09dtpc5">
                Yes — check if I qualify
              </button>
              <button type="button" onClick={onClose} className="w-full rounded-xl px-6 py-3 text-sm font-medium text-white/50 transition-colors hover:text-white/80" data-id="element-53" data-mp="3hjucheh8yrw6">
                No thanks, I'll keep paying more
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}